/* datasets — typed accessor over codegen.json.kaggleDatasets +
 * the dataset-kind entries from codegen.json.drawingStandards.
 *
 * Single source of truth for every dataset the demo graph and the
 * rule library reference. Per CLAUDE.md §1 — declared once in
 * codegen.json, read everywhere via this module. No magic
 * strings ("Kaggle #5 IKEA-SA") anywhere downstream.
 *
 * Two flavours of dataset:
 *   • kaggle  — the 10 tabular / image Kaggle datasets that seed the
 *               demo project graph (SKU, vendor, RERA, schedule, …).
 *   • drawing — the 5 architectural-drawing datasets (CubiCasa5K,
 *               RPLAN, FloorPlanCAD, Matterport3D, 3D-Front / 3D-Future)
 *               registered as `:DrawingStandard` instances in the TTL.
 */

import codegen from "../../../docs/ontology/codegen.json";
import { DRAWING_STANDARDS, type DrawingStandard } from "./drawingStandards";

export interface KaggleDataset {
  id: string;
  /** Stable integer for legacy "Kaggle #N" prose references. */
  n: number;
  label: string;
  url: string;
  rows: string;
  updated: string;
  license: string;
  pillar: string;
}

interface RawKaggle {
  id: string;
  n: number;
  label: string;
  url: string;
  rows: string;
  updated: string;
  license: string;
  pillar: string;
}

const RAW: ReadonlyArray<RawKaggle> = (codegen.kaggleDatasets ?? []) as ReadonlyArray<RawKaggle>;

export const KAGGLE_DATASETS: ReadonlyArray<KaggleDataset> = RAW.map((r) => ({ ...r }));

const BY_N: ReadonlyMap<number, KaggleDataset> =
  new Map(KAGGLE_DATASETS.map((d) => [d.n, d]));
const BY_ID: ReadonlyMap<string, KaggleDataset> =
  new Map(KAGGLE_DATASETS.map((d) => [d.id, d]));

export function kaggleByN(n: number): KaggleDataset | undefined { return BY_N.get(n); }
export function kaggleById(id: string): KaggleDataset | undefined { return BY_ID.get(id); }

/** Pretty citation: "Kaggle #7 · Online Furniture Orders 2025 (MIT)". */
export function kaggleCitation(n: number): string {
  const d = BY_N.get(n);
  if (!d) return `Kaggle #${n}`;
  return `Kaggle #${d.n} · ${d.label} (${d.license})`;
}

/** Architectural drawing datasets — already typed in DRAWING_STANDARDS
 *  with `kind: "dataset"`. Re-exported here for the unified catalog. */
export const DRAWING_DATASETS: ReadonlyArray<DrawingStandard> =
  DRAWING_STANDARDS.filter((s) => s.kind === "dataset");

/** Unified catalog — what the cockpit's "Data sources" panel renders. */
export interface CatalogEntry {
  origin: "kaggle" | "drawing";
  id: string;
  label: string;
  citation: string;
  pillar?: string;
  url?: string;
  rows?: string;
  license?: string;
}

export const DATASET_CATALOG: ReadonlyArray<CatalogEntry> = [
  ...KAGGLE_DATASETS.map((d): CatalogEntry => ({
    origin: "kaggle",
    id: d.id,
    label: d.label,
    citation: kaggleCitation(d.n),
    pillar: d.pillar,
    url: d.url,
    rows: d.rows,
    license: d.license,
  })),
  ...DRAWING_DATASETS.map((d): CatalogEntry => ({
    origin: "drawing",
    id: d.id,
    label: d.label,
    citation: d.citation,
  })),
];
